import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import Breadcrumbs from '../../shared/breadcrumb';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
// ... остальное без изменений
import { Box, Button, Typography, IconButton, Select, MenuItem, ClickAwayListener } from '@mui/material';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GenericTable, { TableColumn } from '../../shared/tables/generic.table';
import { getRoleName, useDeleteUser, useFetchUsers, useResendInvite, UserRoles } from './useFetchUsers.hook';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddNewUserModal from './add-new-user.modal';
import ConfirmModal from '../confirm.modal';
import AssignClientsToUser from './components/assign-clients-to-user.modal';
import apiClient from '../../shared/auth.interceptor';
import Loader from '../../shared/Loader/loader';
import AssignFirmsToUserModal from './components/assign-firms-to-user.modal';
import { IUser } from '../../user/user.interface';
import { getUserRole } from '../../shared/auth.functions';

const columns: TableColumn[] = [
  { id: 'firstName', label: 'First Name', minWidth: 170 },
  { id: 'lastName', label: 'Last Name', minWidth: 100 },
  { id: 'email', label: 'Email', minWidth: 100 },
  { id: 'role', label: 'Role', width: 135 },
  { id: 'companies', label: 'Clients', width: 200 },
  { id: 'actions', label: 'Action', width: 200 }
];

const ROWS_PER_PAGE = 10;

// Единую высоту лучше синхронизировать для всех ячеек
const CELL_HEIGHT = 40;

// Инлайн-редактор текстовой ячейки
// Инлайн-редактор селекта для роли
const ROLE_OPTIONS: Array<{ value: UserRoles; label: string }> = [
  { value: UserRoles.simple_user, label: getRoleName(UserRoles.simple_user) },
  { value: UserRoles.admin, label: getRoleName(UserRoles.admin) },
  { value: UserRoles.super_admin, label: getRoleName(UserRoles.super_admin) }
];

const EditableSelect: React.FC<{
  initialValue: UserRoles | string;
  disabled?: boolean;
  onSave: (value: UserRoles) => Promise<void> | void;
}> = ({ initialValue, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<UserRoles | string>(initialValue);
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const commit = useCallback(
    async (newValue: UserRoles) => {
      if (disabled) return;
      if (newValue === initialValue) {
        setEditing(false);
        return;
      }
      try {
        setSaving(true);
        await onSave(newValue);
        setEditing(false);
      } finally {
        setSaving(false);
      }
    },
    [disabled, initialValue, onSave]
  );

  if (editing && !disabled) {
    return (
      <ClickAwayListener
        onClickAway={() => {
          // Клик мимо — закрываем меню и выходим из режима редактирования
          setMenuOpen(false);
          setEditing(false);
        }}
      >
        <Box>
          <Select
            size="small"
            value={value}
            open={menuOpen}
            onOpen={() => setMenuOpen(true)}
            onClose={() => {
              setMenuOpen(false);
              setEditing(false);
            }}
            onBlur={() => {
              // Потеря фокуса без открытого меню — выходим
              if (!menuOpen) setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'Escape') {
                e.preventDefault();
                setMenuOpen(false);
                setEditing(false);
              }
            }}
            onChange={(e) => {
              const v = e.target.value as UserRoles;
              setValue(v);
              void commit(v);
            }}
            disabled={saving}
            fullWidth
            sx={{
              height: CELL_HEIGHT,
              '& .MuiOutlinedInput-notchedOutline': { top: 0 },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                height: CELL_HEIGHT - 16
              }
            }}
            // В одном слое с таблицей — корректно отрабатывает клик-вне/blur
            MenuProps={{ disablePortal: true, PaperProps: { sx: { maxHeight: 280 } } }}
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem dense key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </ClickAwayListener>
    );
  }

  const label = getRoleName(value as UserRoles) || 'N/A';
  return (
    <Box
      onClick={() => {
        if (!disabled) {
          setEditing(true);
          setMenuOpen(true);
        }
      }}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        minHeight: CELL_HEIGHT,
        height: CELL_HEIGHT,
        display: 'flex',
        alignItems: 'center'
      }}
      title={disabled ? undefined : 'Click to change role'}
    >
      {label}
    </Box>
  );
};

const AdminUsers = () => {
  const currentFirm = useSelector((state: RootState) => state.currentFirm.currentFirm);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const crumbs = [{ title: currentFirm?.name || '' }];
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openAssignClientsToUserModal, setOpenAssignClientsToUserModal] = useState(false);
  const [openAssignFirmsToUserModal, setOpenAssignFirmsToUserModal] = useState(false);

  const [assignClientUser, setAssignClientUser] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { users, loading, error } = useFetchUsers(currentFirm?._id, refreshTrigger);
  const { resendInvite } = useResendInvite();

  const refreshUsers = () => setRefreshTrigger((prev) => prev + 1);
  const handleResendInvite = (email: string) => {
    void resendInvite(email);
  };

  const handleClose = () => {
    setOpenAddUserModal(false);
  };
  const handleCloseAddClient = () => {
    setOpenAssignClientsToUserModal(false);
    setOpenAssignFirmsToUserModal(false);
  };

  // pagination
  const [page, setPage] = useState(0);

  const { requestDelete, confirmDelete, declineDelete, loadingDelete, modal } = useDeleteUser();

  const handleRequestDelete = (userId: string) => {
    requestDelete(userId, currentFirm?._id);
  };
  const handleConfirmYes = async () => {
    const userId = modal.userId;
    const success = await confirmDelete();
    if (success && userId) {
      refreshUsers();
    }
  };
  const handleOpenAssignClientsModal = (user: IUser) => {
    setAssignClientUser(user);
    getUserRole(user, currentFirm) === UserRoles.admin
      ? setOpenAssignFirmsToUserModal(true)
      : setOpenAssignClientsToUserModal(true);
  };

  // Обновление полей пользователя (включая роль)
  const handleUpdateUserField = async (userId: string, field: 'firstName' | 'lastName' | 'role', value: string) => {
    await apiClient.patch(`/v1/user/${userId}`, { [field]: value });
    refreshUsers();
  };

  const isFirmOwner = !!currentUser?._id && currentFirm?.ownerId === currentUser._id;
  // The Super Admin and Admins manage people; nobody but the Super Admin changes the Super Admin.
  const canManage = isFirmOwner || getUserRole(currentUser, currentFirm) === UserRoles.admin;
  const canManageUser = (user: IUser) =>
    canManage && getUserRole(user, currentFirm) !== UserRoles.super_admin && user._id !== currentUser?._id;
  const clientsTextSx = { color: 'info.main', fontSize: 'inherit', textAlign: 'left' };

  // подготовка данных для таблицы
  const formattedUsers = useMemo(() => {
    return users && Array.isArray(users)
      ? users
          .map((user) => ({
            _id: user._id,
            // People change their own name on the Profile page.
            firstName: user.firstName || '-',
            // People change their own name on the Profile page.
            lastName: user.lastName || '-',
            email: user.email || 'N/A',
            role: (
              <EditableSelect
                initialValue={getUserRole(user, currentFirm)}
                disabled
                onSave={(v) => handleUpdateUserField(user._id, 'role', v)}
              />
            ),
            companies: (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                {(() => {
                  switch (getUserRole(user, currentFirm)) {
                    case UserRoles.super_admin:
                      return <Typography sx={clientsTextSx}>All clients</Typography>;
                    case UserRoles.admin: {
                      if (user._id === currentUser?._id) {
                        return (
                          <Typography sx={clientsTextSx}>All clients</Typography>
                        );
                      } else {
                        return (
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleOpenAssignClientsModal(user)}
                            aria-label="assign clients"
                            disabled={!canManageUser(user)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        );
                      }
                    }
                    case UserRoles.simple_user:
                    default:
                      return (
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => handleOpenAssignClientsModal(user)}
                          aria-label="assign clients"
                            disabled={!canManageUser(user)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      );
                  }
                })()}
              </Box>
            ),
            actions: !user.registeredAt ? (
              <div className="d-flex justify-content-between align-items-center flex-nowrap">
                <div className="d-flex justify-content-start flex-nowrap ms-2">
                  {canManageUser(user) && (
                    <DeleteOutlinedIcon
                      color="error"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRequestDelete(user._id)}
                    />
                  )}
                </div>
                <div>
                  <div>Pending</div>
                  {canManageUser(user) && (
                    <span className="clients-list-span ">
                      <a onClick={() => handleResendInvite(user.email)} style={{ cursor: 'pointer' }}>
                        Resend Invite
                      </a>
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-start flex-nowrap ms-2">
                {canManageUser(user) && (
                  <DeleteOutlinedIcon
                    color="error"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRequestDelete(user._id)}
                  />
                )}
              </div>
            )
          }))
      : [];
  }, [users, currentFirm, currentUser?._id, canManage]);

  // При смене текущей фирмы — сбрасываем страницу и перезагружаем пользователей
  useEffect(() => {
    setPage(0);
    setRefreshTrigger((prev) => prev + 1);
  }, [currentFirm?._id]);

  const totalPages = Math.ceil(formattedUsers.length / ROWS_PER_PAGE);
  const paginatedUsers =
    formattedUsers.length > 0 ? formattedUsers.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE) : [];

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setPage((prev) => Math.min(prev + 1, totalPages - 1));

  const inviteNewUser = () => {
    setOpenAddUserModal(true);
  };
  const handleAddUserSuccess = () => {
    refreshUsers();
    setOpenAddUserModal(false);
  };

  return (
    <>
      {(loading || loadingDelete) && <Loader />}

      <div className="mt-2">
        <Breadcrumbs crumbs={crumbs} />
      </div>
      <div className="d-flex justify-content-between mt-4">
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Users</Typography>
        <Button
          hidden={!canManage}
          onClick={() => inviteNewUser()}
          className="primary"
          startIcon={<ControlPointOutlinedIcon />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '18px',
            height: '48px',
            gap: 0
          }}
        >
          Invite New User
        </Button>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}
      {formattedUsers.length > 0 && (
        <Box
          sx={{ position: 'relative', height: 'calc(100vh - 200px)', width: 'calc(100vw - 380px)', overflow: 'auto' }}
        >
          <Box sx={{ position: 'relative', minWidth: '1000px' }}>
            <GenericTable columns={columns} data={paginatedUsers} />
            <Box
              sx={{
                marginTop: '1rem',
                display: 'flex',
                justifyContent: 'right',
                alignItems: 'center',
                padding: '0.5rem 0'
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
              </Typography>
              <IconButton size="small" onClick={handlePrev} disabled={page === 0} aria-label="previous page">
                <ArrowBackIosNewIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
              <IconButton size="small" onClick={handleNext} disabled={page >= totalPages - 1} aria-label="next page">
                <ArrowForwardIosIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
      {
        <AddNewUserModal
          open={openAddUserModal}
          edit={false}
          handleClose={handleClose}
          onSuccess={handleAddUserSuccess}
        ></AddNewUserModal>
      }
      {
        <AssignClientsToUser
          open={openAssignClientsToUserModal}
          handleClose={handleCloseAddClient}
          onSuccess={refreshUsers}
          user={assignClientUser}
        />
      }
      {
        <AssignFirmsToUserModal
          open={openAssignFirmsToUserModal}
          handleClose={handleCloseAddClient}
          onSuccess={refreshUsers}
          user={assignClientUser}
        />
      }
      {
        <ConfirmModal
          open={modal.open}
          handleYes={handleConfirmYes}
          handleNo={declineDelete}
          handleOnClose={declineDelete}
          title="Delete user?"
          message="Are you sure you want to delete this user?"
        />
      }
    </>
  );
};

export default AdminUsers;
